import api from './axiosConfig';
import {
  BgvCaseDto,
  BgvCaseDetailDto,
  BgvDashboardDto,
  BgvVendorDto,
  BgvVerificationTypeDto,
  CreateBgvCase,
  CreateBgvVendor,
  UpdateBgvVendor,
  CreateBgvVerificationType,
  CreateBgvDiscrepancy,
  BgvCaseStatus,
  BgvFinalDecision,
  BgvTaskStatus,
  BgvDocumentStatus,
} from '../types/bgvTypes';

export const bgvApi = {
  // Dashboard
  getDashboard: async (fromDate?: string, toDate?: string): Promise<BgvDashboardDto> => {
    const params: Record<string, string> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    const response = await api.get<BgvDashboardDto>('/bgv/dashboard', { params });
    return response.data;
  },

  // Cases
  getCases: async (filters?: {
    status?: BgvCaseStatus;
    finalDecision?: BgvFinalDecision;
    employeeId?: string;
    assignedToId?: string;
    vendorId?: string;
    fromDate?: string;
    toDate?: string;
    searchTerm?: string;
  }): Promise<BgvCaseDto[]> => {
    const response = await api.get<BgvCaseDto[]>('/bgv/cases', { params: filters });
    return response.data;
  },

  getAssignedCases: async (filters?: {
    status?: BgvCaseStatus;
    finalDecision?: BgvFinalDecision;
    fromDate?: string;
    toDate?: string;
    searchTerm?: string;
  }): Promise<BgvCaseDto[]> => {
    const response = await api.get<BgvCaseDto[]>('/bgv/assigned-cases', { params: filters });
    return response.data;
  },

  getCaseById: async (id: string): Promise<BgvCaseDetailDto> => {
    const response = await api.get<BgvCaseDetailDto>(`/bgv/cases/${id}`);
    return response.data;
  },

  getAssignedCaseById: async (id: string): Promise<BgvCaseDetailDto> => {
    const response = await api.get<BgvCaseDetailDto>(`/bgv/assigned-cases/${id}`);
    return response.data;
  },

  createCase: async (data: CreateBgvCase): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/bgv/cases', data);
    return response.data;
  },

  updateCaseStatus: async (id: string, status: BgvCaseStatus, remarks?: string): Promise<void> => {
    await api.put(`/bgv/cases/${id}/status`, { status, remarks });
  },

  updateFinalDecision: async (id: string, finalDecision: BgvFinalDecision, remarks?: string): Promise<void> => {
    await api.put(`/bgv/cases/${id}/decision`, { finalDecision, remarks });
  },

  submitVendorDecision: async (id: string, decision: BgvFinalDecision, remarks?: string): Promise<void> => {
    await api.put(`/bgv/assigned-cases/${id}/decision`, { decision, remarks });
  },

  // Tasks
  updateTaskStatus: async (taskId: string, status: BgvTaskStatus, remarks?: string): Promise<void> => {
    await api.put(`/bgv/tasks/${taskId}/status`, { status, remarks });
  },

  // Vendor-specific task status update. Discrepancies must use createAssignedDiscrepancy.
  updateVendorTaskStatus: async (taskId: string, status: BgvTaskStatus, remarks?: string): Promise<void> => {
    await api.put(`/bgv/assigned-tasks/${taskId}/status`, { status, remarks });
  },

  // Documents
  uploadDocument: async (taskId: string, file: File): Promise<{ id: string; filePath: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ id: string; filePath: string }>(
      `/bgv/tasks/${taskId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Vendor-specific document upload
  vendorUploadDocument: async (taskId: string, file: File): Promise<{ id: string; filePath: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ id: string; filePath: string }>(
      `/bgv/assigned-tasks/${taskId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  updateDocumentStatus: async (documentId: string, status: BgvDocumentStatus, reviewRemarks?: string): Promise<void> => {
    await api.put(`/bgv/documents/${documentId}/status`, { status, reviewRemarks });
  },

  // Discrepancies
  createDiscrepancy: async (data: CreateBgvDiscrepancy): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/bgv/discrepancies', data);
    return response.data;
  },

  createAssignedDiscrepancy: async (data: CreateBgvDiscrepancy): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/bgv/assigned-discrepancies', data);
    return response.data;
  },

  // Vendors
  getVendors: async (isActive?: boolean, searchTerm?: string): Promise<BgvVendorDto[]> => {
    const params: Record<string, string | boolean> = {};
    if (isActive !== undefined) params.isActive = isActive;
    if (searchTerm) params.searchTerm = searchTerm;

    try {
      const response = await api.get<BgvVendorDto[]>('/bgv/vendors', { params });
      return response.data;
    } catch (error: any) {
      const statusCode = error?.response?.status;
      const canRetryWithoutFilters = statusCode === 500 && Object.keys(params).length > 0;

      if (!canRetryWithoutFilters) {
        throw error;
      }

      const fallbackResponse = await api.get<BgvVendorDto[]>('/bgv/vendors');
      let fallbackData = fallbackResponse.data;

      if (isActive !== undefined) {
        fallbackData = fallbackData.filter((vendor) => vendor.isActive === isActive);
      }
      if (searchTerm) {
        const normalizedSearch = searchTerm.toLowerCase();
        fallbackData = fallbackData.filter((vendor) =>
          `${vendor.name} ${vendor.contactPerson ?? ''} ${vendor.email ?? ''} ${vendor.phone ?? ''}`
            .toLowerCase()
            .includes(normalizedSearch)
        );
      }

      return fallbackData;
    }
  },

  createVendor: async (data: CreateBgvVendor): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/bgv/vendors', data);
    return response.data;
  },

  updateVendor: async (id: string, data: UpdateBgvVendor): Promise<void> => {
    await api.put(`/bgv/vendors/${id}`, data);
  },

    deletevendors: async (id: string): Promise<void> => {
  const res=await api.delete(`/bgv/vendors/${id}`);
  return res.data;
},

  // Verification Types
  getVerificationTypes: async (isActive?: boolean): Promise<BgvVerificationTypeDto[]> => {
    const params: Record<string, boolean> = {};
    if (isActive !== undefined) params.isActive = isActive;

    try {
      const response = await api.get<BgvVerificationTypeDto[]>('/bgv/verification-types', { params });
      return response.data;
    } catch (error: any) {
      const statusCode = error?.response?.status;
      const canRetryWithoutFilters = statusCode === 500 && Object.keys(params).length > 0;

      if (!canRetryWithoutFilters) {
        throw error;
      }

      const fallbackResponse = await api.get<BgvVerificationTypeDto[]>('/bgv/verification-types');
      const fallbackData = fallbackResponse.data;
      if (isActive === undefined) {
        return fallbackData;
      }

      return fallbackData.filter((verificationType) => verificationType.isActive === isActive);
    }
  },

  createVerificationType: async (data: CreateBgvVerificationType): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/bgv/verification-types', data);
    return response.data;
  },

  updateVerificationType: async (
  id: string,
  data: { id: string; name: string; description?: string; isActive: boolean }
): Promise<void> => {
  await api.put(`/bgv/verification-types/${id}`, data);
},

deleteVerificationType: async (id: string): Promise<void> => {
  const res=await api.delete(`/bgv/verification-types/${id}`);
  return res.data;
},

  resolveDiscrepancy: (discrepancyId: string) =>
    api.patch(`/bgv/discrepancies/${discrepancyId}/resolve`),
};
